import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { connectSocket } from './socketClient';
import { playAlertChime } from './alertSound';

const AUTO_DISMISS_MS = 9000;
const MAX_STACK = 4;

/**
 * Corner "bubble" notifications for system admins — a new bug report or a new
 * announcement reply, shown as a small transient toast (bottom-left, clear of
 * the help widget / announcement dock in the bottom-right). Complements the
 * WebSocket panels and OS web push: this is the glanceable in-app nudge.
 */
export function useAdminBubbles() {
    const { user, token } = useAuth();
    const isAdmin = user?.role === 'system_admin';
    const [bubbles, setBubbles] = useState([]);
    const timers = useRef({});

    const dismiss = useCallback((id) => {
        setBubbles((prev) => prev.filter((b) => b.id !== id));
        clearTimeout(timers.current[id]);
        delete timers.current[id];
    }, []);

    const push = useCallback((b) => {
        setBubbles((prev) => [...prev.slice(-(MAX_STACK - 1)), b]);
        timers.current[b.id] = setTimeout(() => dismiss(b.id), AUTO_DISMISS_MS);
        playAlertChime();
    }, [dismiss]);

    useEffect(() => {
        if (!isAdmin || !token) return undefined;
        const socket = connectSocket(token);
        if (!socket) return undefined;

        const onBug = (p) => push({
            id: `bug-${p.id}-${Date.now()}`,
            kind: 'bug',
            title: 'New bug report',
            detail: `${p.reportedByName}${p.hasAudio ? ' · 🎤 voice note' : ''}${p.note ? ` — ${p.note}` : ''}`,
            target: '/admin/users'
        });
        const onReply = (p) => push({
            id: `reply-${p.id}-${Date.now()}`,
            kind: 'reply',
            title: 'New announcement reply',
            detail: `${p.userName} — ${p.text}`,
            openDock: true
        });

        socket.on('bug-report:new', onBug);
        socket.on('announcement:response', onReply);
        return () => {
            socket.off('bug-report:new', onBug);
            socket.off('announcement:response', onReply);
        };
    }, [isAdmin, token, push]);

    useEffect(() => () => {
        Object.values(timers.current).forEach(clearTimeout);
    }, []);

    return { bubbles: isAdmin ? bubbles : [], dismiss };
}
